/* eslint-disable react/no-danger */
import React, { useEffect, useState } from "react";
import PropTypes from "prop-types";
import { useIntl } from "react-intl";
import styled from "styled-components";

import locales from "../locales";
import PaymentOptionSelector from "./PaymentOptionSelector";
import Text from "./Text";
import Button from "./Button";

const ProductOptionsContainerWrapper = styled.div`
  .ProductOptionsContainer_Header {
    margin-top: 32px;
    margin-bottom: 24px;

    & > h2 {
      font-size: 20px !important;
    }
  }

  .education-cta-fixed {
    @media screen and (max-width: ${({ theme }) =>
        theme.breakpoints.mobile}px) {
      position: fixed;
      left: 0;
      right: 0;
      bottom: 0;
      z-index: 10;
      background: ${({ theme }) => theme.palette.white};
      padding: 16px !important;
      border-top: 1px solid #f4f4f4;
      box-sizing: border-box;
    }
  }
`;

const EducationCtaButton = styled(Button)`
  border-radius: 100px !important;
  background: #141414 !important;
  border-color: #141414 !important;
  &:hover,
  &:active {
    background: #333333 !important;
    border-color: #333333 !important;
  }
`;

const AVAILABLE_PRODUCTS = ["pp3", "pp6", "pp9", "sp1", "fp1"];

const ProductOptionsContainer = ({
  merchantProducts,
  creditAgreements,
  totalAmount,
  checkoutRedirect,
  isEducationMerchant,
}) => {
  const intl = useIntl();
  const [loading, setLoading] = useState(false);
  const [selectedProduct, setSelectedProduct] = useState(null);

  const handleSubmit = async (product) => {
    setLoading(true);
    const result = await checkoutRedirect(product);
    if (!result) {
      window.location.reload();
    }
  };

  const availableProducts = AVAILABLE_PRODUCTS.filter(
    (product) =>
      merchantProducts.includes(product) && creditAgreements?.[product]
  );

  const isSingleOption = availableProducts.length === 1;
  const hideOptionsList = isEducationMerchant && isSingleOption;

  useEffect(() => {
    if (isSingleOption && selectedProduct !== availableProducts[0]) {
      setSelectedProduct(availableProducts[0]);
    }
  }, [availableProducts]);

  const getProductCopies = (product) => {
    if (["pp3", "pp6", "pp9"].includes(product)) {
      const freeCreditAgreements = (creditAgreements[product] || [])
        .filter((ca) => ca.cost_of_credit.value === 0)
        .sort((a, b) => b.instalment_count - a.instalment_count);

      const hasFreeAgreements = freeCreditAgreements.length > 0;
      const freeAgreementInstalmentsCount = hasFreeAgreements
        ? freeCreditAgreements[0].instalment_count
        : null;

      const sortedCreditAgreements = (creditAgreements[product] || []).sort(
        (a, b) => a.instalment_total.value - b.instalment_total.value
      );
      const totalCost = sortedCreditAgreements[0]?.instalment_total.string;

      const hasFreeAgreementsSubtitle = hasFreeAgreements
        ? intl.formatMessage(locales.instalments_free, {
            freeAgreementInstalmentsCount,
          })
        : "";

      return {
        title: intl.formatMessage(locales.pay_in_instalments),
        subtitle: intl.formatMessage(locales.pay_in_instalments_subtitle, {
          totalCost,
          hasFreeAgreementsSubtitle,
        }),
      };
    }

    if (product === "sp1") {
      return {
        title: intl.formatMessage(locales.split_in_three_without_cost),
        subtitle: "",
      };
    }

    if (product === "fp1") {
      return {
        title: intl.formatMessage(locales.full_payment),
        subtitle: "",
      };
    }

    return { title: "", subtitle: "" };
  };

  return (
    <ProductOptionsContainerWrapper>
      {!hideOptionsList && (
        <>
          <div className="ProductOptionsContainer_Header">
            <Text heading2>
              {availableProducts.length === 1
                ? intl.formatMessage(locales.payment_method)
                : intl.formatMessage(locales.choose_how_to_pay, {
                    totalAmount,
                  })}
            </Text>
          </div>
          {availableProducts.map((product) => {
            const { title, subtitle } = getProductCopies(product);
            return (
              <PaymentOptionSelector
                key={product}
                product={product}
                title={title}
                subtitle={subtitle}
                selected={selectedProduct === product}
                onClick={() => setSelectedProduct(product)}
                isInteractive={availableProducts.length > 1}
              />
            );
          })}
        </>
      )}
      <div
        className={`cta-button-wrapper${
          hideOptionsList ? " education-cta-fixed" : ""
        }`}
      >
        {hideOptionsList ? (
          <EducationCtaButton
            fullWidth
            data-testid="next-button"
            disabled={loading || !selectedProduct}
            onClick={() => handleSubmit(selectedProduct)}
          >
            {intl.formatMessage(locales.education_checkout_cta_button)}
          </EducationCtaButton>
        ) : (
          <Button
            fullWidth
            data-testid="next-button"
            disabled={loading || !selectedProduct}
            onClick={() => handleSubmit(selectedProduct)}
          >
            {intl.formatMessage(locales.shopper_select_product_option_button)}
          </Button>
        )}
      </div>
    </ProductOptionsContainerWrapper>
  );
};

ProductOptionsContainer.propTypes = {
  merchantProducts: PropTypes.arrayOf(PropTypes.string).isRequired,
  creditAgreements: PropTypes.object.isRequired,
  totalAmount: PropTypes.string.isRequired,
  checkoutRedirect: PropTypes.func.isRequired,
  isEducationMerchant: PropTypes.bool,
};

ProductOptionsContainer.defaultProps = {
  isEducationMerchant: false,
};

export default ProductOptionsContainer;
